import axios from 'axios';
import { apiClient } from './client.api';
import { CourseOption } from '../../types/course.response';
import { MutationResult } from '../../types/mutation.response';
import { putUserItem, removeUserItemById } from '../userCourseCache';

export interface CourseRequirementOverride {
    requirementId: string;
    nodeId: string;
    overrideType: 'FORCE_APPLY' | 'FORCE_UNAPPLY';
}

/** 契约 §4.3:已加载的 programIds 拼进 ?fulfillment=,后端受影响时在响应内嵌详情档 */
export function fulfillmentParams(programIds?: string[]): { fulfillment: string } | undefined {
    return programIds?.length ? { fulfillment: programIds.join(',') } : undefined;
}

export const userCoursesApi = {
    getCourses: async (): Promise<CourseOption[]> => {
        try {
            const response = await apiClient.get(`/user-courses`);
            return response.data.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to get the course');
            }
            throw error;
        }
    },

    addCourse: async (
        courseId: string,
        topic: string,
        isScheduled: boolean,
        isSaved: boolean,
        semester: string | null,
        creditsReceived: number | null,
        classSectionIds: number[],
        overrides: CourseRequirementOverride[],
        fulfillmentProgramIds?: string[],
    ): Promise<MutationResult<CourseOption>> => {
        try {
            const response = await apiClient.post('/user-courses', {
                courseId,
                topic,
                isScheduled,
                isSaved,
                semester,
                creditsReceived,
                classSectionIds,
                overrides,
            }, { params: fulfillmentParams(fulfillmentProgramIds) });
            putUserItem(response.data.data); // §3.4-b 双写
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to add courses to schedule');
            }
            throw error;
        }
    },

    updateCourse: async (
        userCourseId: number,
        updates: {
            isScheduled?: boolean;
            isSaved?: boolean;
            semester?: string | null;
            creditsReceived?: number | null;
            classSectionIds?: number[];
            overrides?: CourseRequirementOverride[];
        },
        fulfillmentProgramIds?: string[],
    ): Promise<MutationResult<CourseOption>> => {
        try {
            const response = await apiClient.patch(`/user-courses/${userCourseId}`, updates, {
                params: fulfillmentParams(fulfillmentProgramIds),
            });
            putUserItem(response.data.data); // §3.4-b 双写
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to update the course');
            }
            throw error;
        }
    },

    deleteCourse: async (
        userCourseId: number,
        fulfillmentProgramIds?: string[],
    ): Promise<MutationResult<CourseOption>> => {
        try {
            const response = await apiClient.delete(`/user-courses/${userCourseId}`, {
                params: fulfillmentParams(fulfillmentProgramIds),
            });
            removeUserItemById(userCourseId); // §3.4-b 双写(删)
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to delete the course');
            }
            throw error;
        }
    },

    addOverride: async (
        userCourseId: number,
        override: CourseRequirementOverride,
        fulfillmentProgramIds?: string[],
    ): Promise<MutationResult<CourseRequirementOverride>> => {
        try {
            const response = await apiClient.put(`/user-courses/${userCourseId}/requirement-overrides`, override, {
                params: fulfillmentParams(fulfillmentProgramIds),
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to add the override');
            }
            throw error;
        }
    },

    deleteOverride: async (
        userCourseId: number,
        requirementId: string,
        fulfillmentProgramIds?: string[],
    ): Promise<MutationResult<CourseRequirementOverride>> => {
        try {
            const response = await apiClient.delete(
                `/user-courses/${userCourseId}/requirement-overrides/${requirementId}`,
                { params: fulfillmentParams(fulfillmentProgramIds) },
            );
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                throw new Error(error.response?.data?.error || 'Failed to delete the override');
            }
            throw error;
        }
    },
};
